<script setup lang="ts">
import { useCurrentAccountManager } from '@/api/queries/accountManagers';
import type { ExclusiveContacts } from '~/types/exclusives';
import ExclusiveManagerCard from './ExclusiveManagerCard.vue';

// Sidebar contacts on a 100% Commission project (meeting 2026-11): the project's reps, plus the viewer's own Account
// Manager when the admin ticked it. Projects without reps keep the global Exclusive Manager card.
const { contacts } = defineProps<{
  contacts: ExclusiveContacts | null;
}>();

const { t } = useI18n();
const { data: accountManager } = useCurrentAccountManager();

type Person = {
  key: string;
  name: string;
  label: string;
  phone: string;
  photoUrl: string | null;
  whatsappUrl: string;
  callUrl: string;
};

const linksFor = (phone: string) => {
  const digits = phone.replace(/\D/g, '');
  return { whatsappUrl: `https://wa.me/${digits}`, callUrl: `tel:+${digits}` };
};

const reps = computed<Person[]>(() =>
  (contacts?.reps ?? []).map((rep, i) => ({
    key: `rep-${i}`,
    name: rep.name,
    label: rep.role ?? t('exclusives.contacts.rep'),
    phone: rep.phone,
    photoUrl: rep.photoUrl,
    whatsappUrl: rep.whatsappUrl,
    callUrl: rep.callUrl,
  })),
);

const accountManagerPerson = computed<Person | null>(() => {
  const am = accountManager.value;
  if (!contacts?.includeAccountManager || !am?.phone) return null;
  return {
    key: 'account-manager',
    name: `${am.firstName} ${am.lastName}`.trim(),
    label: t('exclusives.contacts.accountManager'),
    phone: am.phone,
    photoUrl: am.photoUrl,
    ...linksFor(am.phone),
  };
});

const people = computed(() => [...reps.value, ...(accountManagerPerson.value ? [accountManagerPerson.value] : [])]);

const failedPhotos = ref(new Set<string>());
const initials = (name: string) => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return `${parts[0]?.[0] ?? ''}${parts.length > 1 ? (parts[parts.length - 1][0] ?? '') : ''}`.toUpperCase();
};
const onPhotoError = (key: string) => {
  failedPhotos.value = new Set([...failedPhotos.value, key]);
};
</script>

<template>
  <!-- No reps listed: the global Exclusive Manager stays the project contact. -->
  <ExclusiveManagerCard v-if="!reps.length" />
  <section
    v-if="people.length"
    :class="['rounded-[20px] bg-bg p-5', { 'mt-4': !reps.length }]"
    data-testid="exclusive-project-contacts"
  >
    <p class="mb-3 font-mono text-[10px] font-bold uppercase tracking-[0.12em] text-black-60">
      {{ t('exclusives.contacts.title') }}
    </p>
    <ul class="flex flex-col gap-4">
      <li
        v-for="person in people"
        :key="person.key"
        class="flex flex-col gap-3"
      >
        <div class="flex min-w-0 items-center gap-3">
          <img
            v-if="person.photoUrl && !failedPhotos.has(person.key)"
            :src="person.photoUrl"
            alt=""
            class="size-11 shrink-0 rounded-full object-cover"
            @error="onPhotoError(person.key)"
          />
          <div
            v-else
            class="inline-flex size-11 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-gold-primary-100 to-gold-primary-120 font-display text-[15px] font-extrabold text-black-100"
            aria-hidden="true"
          >
            {{ initials(person.name) }}
          </div>
          <div class="min-w-0">
            <p class="text-[11px] font-semibold text-black-60">{{ person.label }}</p>
            <p class="truncate font-serif text-[16px] font-semibold text-black-100">{{ person.name }}</p>
            <p
              class="text-[13px] font-semibold text-black-80"
              dir="ltr"
            >
              {{ person.phone }}
            </p>
          </div>
        </div>
        <div class="flex gap-2">
          <a
            :href="person.whatsappUrl"
            target="_blank"
            rel="noopener"
            class="inline-flex h-10 flex-1 items-center justify-center gap-2 rounded-[10px] bg-[#25D366] px-3 text-[13px] font-bold text-white transition-colors hover:bg-[#1EB454]"
          >
            <UIIcon
              name="whatsapp"
              size="sm"
            />
            {{ t('exclusives.manager.whatsapp') }}
          </a>
          <a
            :href="person.callUrl"
            class="inline-flex h-10 flex-1 items-center justify-center gap-2 rounded-[10px] border border-black/[0.08] bg-white px-3 text-[13px] font-semibold text-black-100 transition-colors hover:bg-black-05"
            :aria-label="t('exclusives.manager.callAria', { name: person.name, phone: person.phone })"
          >
            <UIIcon
              name="phone"
              size="sm"
            />
            {{ t('exclusives.manager.call') }}
          </a>
        </div>
      </li>
    </ul>
  </section>
</template>
